import type * as React from 'react';
import type { intlShape } from 'react-intl';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import '@duel/core';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'react-router-redux';
import { setSignOnType } from '~/actions/LayoutActions';
import * as BlueprintManagementPageSlice from '~/Slices/BlueprintManagementPageSlice';
import type { IOgmAiSavings } from '~/Slices/OGMAI/SuggestionsSlice';
import * as SuggestionsSlice from '~/Slices/OGMAI/SuggestionsSlice';
import { OGMAIViewTab } from '~/Slices/schema';
import { RestrictionsActions } from '../../actions';
import * as UserActions from '../../actions/AdminActions/UsersActions';
import * as FetchAction from '../../actions/FetchAction';
import type { IAppState } from '../../appstate';
import { Container } from '../../components/Bulma';
import Button from '../../components/DSX/Button';
import Modal from '../../components/DSX/Modal';
import DSXNavBar from '../../components/DSX/NavigationBar';
import { customerNotesPageLoaded } from '../../CustomerNotes/customer-notes.duck';
import type { IConnectedComponentProps } from '../../domain';
import { Endpoints, Routes } from '../../helpers';
import { key } from '../../helpers/LocalStorage';
import route from '../../helpers/Routes';
import { getSession } from '~/helpers/Session';
import { configureZendeskWidget } from '~/helpers/ZendeskWidget';
import { key as messageKey } from '../../messages';

export const KEEPALIVE_START = '@ta:KEEPALIVE';

export const TIMEOUT_REFRESH = '@ta:TIMEOUT_REFRESH';
export const TIMEOUT_WARNING = '@ta:TIMEOUT_WARNING';

export const TIMER_UPDATE = '@ta:TIMER_UPDATE';
export const TIMER_CLEAR = '@ta:TIMER_CLEAR';
export const TIMER_START = '@ta:TIMER_START';

export interface ILayoutProps {
	isOpen: boolean;
	timeoutRemaining: number;
	isSingleSignOn: boolean;
	optimizationSavings: IOgmAiSavings;
}

export interface IConnectedLayoutProps
	extends IConnectedComponentProps,
		ILayoutProps {
	body: React.ReactElement<any>;
	navbar?: React.ReactElement<any>;
	footer?: React.ReactElement<any>;
	intl: intlShape;
}

function Layout(props: IConnectedLayoutProps) {
	const dispatch = useDispatch();
	const maxZendeskWidgetAge = 3400;

	useEffect(() => {
		dispatch(setSignOnType());
		dispatch(SuggestionsSlice.getOptimizationSavings());
	}, []);

	useEffect(() => {
		if (getSession()?.userId > 0) {
			configureZendeskWidget(maxZendeskWidgetAge);

			const intervalId = window.setInterval(() => {
				configureZendeskWidget(maxZendeskWidgetAge);
			}, maxZendeskWidgetAge * 1000);

			return () => {
				window.clearInterval(intervalId);
			};
		}
	}, [maxZendeskWidgetAge]);

	const calculateTimeLeft = () => {
		const expiresAt = new Date(
			parseInt(window.localStorage.getItem(key.expiresAt))
		);

		const timeRightNow = new Date();

		const difference = expiresAt.getTime() - timeRightNow.getTime();

		return difference;
	};

	let dialog_timer: number;
	let timer: number;

	const useLogoutTimer = () => {
		useEffect(() => {
			if (!props.isOpen) {
				timer = window.setInterval(() => {
					let timeLeft = calculateTimeLeft();
					if (timeLeft <= 0) {
						let inactivityCountdown = 60000;

						dispatch({
							type: TIMER_UPDATE,
							payload: inactivityCountdown,
						});

						dispatch({
							type: TIMEOUT_WARNING,
						});

						dialog_timer = window.setInterval(() => {
							timeLeft = calculateTimeLeft();
							if (timeLeft > 0) {
								dispatch({
									type: TIMEOUT_REFRESH,
								});
								window.clearInterval(dialog_timer);
							}
							inactivityCountdown -= 1000;
							dispatch({
								type: TIMER_UPDATE,
								payload: inactivityCountdown,
							});
							if (inactivityCountdown <= 0) {
								const isSingleSignOn =
									window.localStorage
										.getItem(key.isSingleSignOn)
										.toLocaleLowerCase() === 'true';
								window.location.href = isSingleSignOn
									? Routes.route.SSOExpiredSession()
									: Endpoints.endpoint.logout;
							}
						}, 1000);
						window.clearInterval(timer);
					}
				}, 5000);
			}

			return () => {
				window.clearInterval(timer);
				window.clearInterval(dialog_timer);
			};
		}, [dispatch, props.isOpen]);
	};

	useLogoutTimer();

	// TODO(chrisw): All these dispatched actions should live in the Layout duck.
	const brandClicked = (event: React.MouseEvent<HTMLAnchorElement>) => {
		event.preventDefault();
		props.dispatch(push('/'));
	};
	const brandClickedNoCustomer = (
		event: React.MouseEvent<HTMLAnchorElement>
	) => {
		event.preventDefault();
		props.dispatch(push('/customers'));
	};

	const globalSearchClicked = () => {
		props.dispatch(push('/search'));
	};

	const globalActivityClicked = () => {
		props.dispatch(push('/activity'));
	};

	const onOGMAIClicked = () => {
		const { dispatch } = props;
		dispatch(SuggestionsSlice.getOrderGuideOptions());
		dispatch(SuggestionsSlice.getOptimizations());
		dispatch(SuggestionsSlice.getOptimizationSavings());
		dispatch(SuggestionsSlice.getGroupingSavings());
		dispatch(push(`/ogmai/${OGMAIViewTab.suggestedOptimization}`));
	};

	const restrictionLinkClicked = () => {
		const { dispatch } = props;
		dispatch(RestrictionsActions.resetProductValidationAndLookup());
	};

	const notesLinkClicked = () => {
		const { dispatch } = props;
		dispatch(customerNotesPageLoaded());
	};

	const masterOrderGuidesLinkClicked = () => {
		const { dispatch } = props;
		dispatch(BlueprintManagementPageSlice.getBlueprints({}));
	};

	const onTimeoutContinue = () => {
		const { dispatch } = props;

		dispatch(
			FetchAction.CreateFetchAction({
				url: Endpoints.endpoint.keepAlive,
				startAction: KEEPALIVE_START,
			})
		);
	};

	const onTimeoutExitApplication = () => {
		const { isSingleSignOn } = props;

		window.location.href = isSingleSignOn
			? route.SSOExpiredSession()
			: route.logout();
	};

	const onUserAdminClicked = () => {
		if (window.location.pathname !== '/admin/users') {
			props.dispatch(UserActions.resetUserFilters());
		}
	};

	const { body, navbar, footer, intl, isSingleSignOn } = props;

	const copyrightMessage = intl.formatMessage({
		id: messageKey((x) => x.copyrightMessages.copyright),
	});
	const confidentialityNotice = intl.formatMessage({
		id: messageKey((msg) => msg.copyrightMessages.confidentialityNotice),
	});
	const termsOfUse = intl.formatMessage({
		id: messageKey((msg) => msg.privacyMessages.termsOfUse),
	});
	const privacyPolicy = intl.formatMessage({
		id: messageKey((msg) => msg.privacyMessages.privacyPolicy),
	});
	const cookiePolicy = intl.formatMessage({
		id: messageKey((msg) => msg.privacyMessages.cookiePolicy),
	});
	const californiaCollectionNotice = intl.formatMessage({
		id: messageKey((msg) => msg.privacyMessages.californiaCollectionNotice),
	});
	const privacyChoices = intl.formatMessage({
		id: messageKey((msg) => msg.privacyMessages.privacyChoices),
	});

	const copyright = (
		<p className="text-muted">
			{copyrightMessage} <b>{confidentialityNotice}</b>
		</p>
	);
	const terms = (
		<a
			href="https://www.directsupply.com/legal/terms-of-use/"
			rel="noreferrer"
			target="_blank"
		>
			{termsOfUse}
		</a>
	);
	const privacy = (
		<a
			href="https://www.directsupply.com/legal/privacy-policy"
			rel="noreferrer"
			target="_blank"
		>
			{privacyPolicy}
		</a>
	);
	const cookie = (
		<a
			href="https://www.directsupply.com/legal/cookie-policy"
			rel="noreferrer"
			target="_blank"
		>
			{cookiePolicy}
		</a>
	);
	const californiaCollection = (
		<a
			href="https://www.directsupply.com/legal/privacy-policy#california-collection-notice"
			rel="noreferrer"
			target="_blank"
		>
			{californiaCollectionNotice}
		</a>
	);
	const privacyChoice = (
		<a href="#" onClick={() => (window as any).OneTrust.ToggleInfoDisplay()}>
			{privacyChoices}
			{'     '}
			<img
				src="/images/opt-out.png"
				style={{ height: '1em', display: 'inline' }}
			/>
		</a>
	);
	const divider = '     \u2022     ';
	const privacyLine = (
		<p className="text-muted">
			{terms}
			{divider}
			{privacy}
			{divider}
			{cookie}
			{divider}
			{californiaCollection}
			{divider}
			{privacyChoice}
		</p>
	);

	const navbarComponent =
		navbar === undefined ? (
			<DSXNavBar
				customerName={window.localStorage.getItem(key.customerName)}
				hideSignOutLink={isSingleSignOn}
				onRestrictionsLinkClicked={restrictionLinkClicked}
				onNotesLinkClicked={notesLinkClicked}
				onMasterOrderGuidesLinkClicked={masterOrderGuidesLinkClicked}
				onUserAdminClicked={onUserAdminClicked}
				onOGMAIClicked={onOGMAIClicked}
				onBrandClicked={brandClicked}
				onBrandClickedNoCustomer={brandClickedNoCustomer}
				optimizationSavings={props.optimizationSavings}
				onGlobalSearchClicked={globalSearchClicked}
				onGlobalActivityClicked={globalActivityClicked}
				intl={props.intl}
			/>
		) : (
			navbar
		);
	const footerComponent =
		footer === undefined ? (
			<footer className="footer">
				<div className="container text-center-xs">{copyright}</div>
				<div className="container text-center-xs">{privacyLine}</div>
			</footer>
		) : (
			footer
		);

	const autoSignOutButtons = [
		<Button
			style="danger"
			key={0}
			onClick={onTimeoutExitApplication}
			value="Sign Out"
		/>,
		<Button style="primary" key={1} onClick={onTimeoutContinue} value="Okay" />,
	];

	return (
		<div className="wrapper">
			{navbarComponent}
			<Modal
				show={props.isOpen}
				onCancel={onTimeoutContinue}
				header="Warning!"
				body={`You will automatically sign out in ${props.timeoutRemaining} seconds. Press 'Okay' to remain signed in.`}
				footerButtons={autoSignOutButtons}
			/>
			<Container className="main" isFullHd={true}>
				{body}
			</Container>
			{footerComponent}
		</div>
	);
}

const mapStateToProps = (state: IAppState): ILayoutProps => {
	return {
		isOpen: state.sessionTimeout.timeoutModal.isOpen,
		timeoutRemaining: state.sessionTimeout.timerState.timeRemainingMS / 1000,
		isSingleSignOn: state.layout.isSingleSignOn,
		optimizationSavings: state.OGMAISuggestions.optimizationSavings,
	};
};

export default injectIntl(connect(mapStateToProps)(Layout));
